import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Archive, Pencil, RotateCcw, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { EmptyState } from "@/components/empty-state";
import {
  getMyAccount, listAccountWorkspaces, renameWorkspace, setWorkspaceActive,
} from "@/lib/settings.functions";

export const Route = createFileRoute("/_authenticated/settings/workspaces")({
  component: WorkspaceSettings,
});

function WorkspaceSettings() {
  const queryClient = useQueryClient();
  const fetchAccount = useServerFn(getMyAccount);
  const fetchWs = useServerFn(listAccountWorkspaces);
  const rename = useServerFn(renameWorkspace);
  const setActive = useServerFn(setWorkspaceActive);

  const { data: acctData } = useQuery({ queryKey: ["my-account"], queryFn: () => fetchAccount() });
  const account = acctData?.account;
  const { data, isLoading } = useQuery({
    queryKey: ["account-workspaces", account?.id],
    queryFn: () => fetchWs({ data: { account_id: account!.id } }),
    enabled: !!account?.id,
  });

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["account-workspaces", account?.id] });

  const handleRename = async (id: string) => {
    if (!editName.trim()) return;
    try {
      await rename({ data: { workspace_id: id, program_name: editName.trim() } });
      toast.success("Renamed");
      setEditingId(null);
      refresh();
    } catch (e: any) { toast.error(e?.message ?? "Failed"); }
  };

  const handleArchive = async (id: string, is_active: boolean) => {
    try {
      await setActive({ data: { workspace_id: id, is_active } });
      refresh();
    } catch (e: any) { toast.error(e?.message ?? "Failed"); }
  };

  if (!account || isLoading) {
    return <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">Loading…</div>;
  }

  const ws = data?.workspaces ?? [];
  const limitLabel = data?.unlimited ? "Unlimited" : `${ws.length} / ${data?.limit ?? 15}`;

  return (
    <div className="rounded-xl border border-border bg-card p-6">
      <div className="mb-4 flex items-center justify-between gap-4">
        <div>
          <h2 className="text-base font-medium">Workspaces</h2>
          <p className="mt-1 text-sm text-muted-foreground">Programs you're tracking. {limitLabel} used.</p>
        </div>
      </div>

      {ws.length === 0 ? (
        <EmptyState title="No workspaces yet" description="Create a workspace from the Workspaces page to start tracking a program." />
      ) : (
        <div className="divide-y divide-border">
          {ws.map((w: any) => (
            <div key={w.id} className="flex items-center justify-between gap-4 py-3">
              <div className="flex-1 min-w-0">
                {editingId === w.id ? (
                  <div className="flex items-center gap-2">
                    <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="max-w-xs" />
                    <Button size="sm" onClick={() => handleRename(w.id)}>Save</Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>Cancel</Button>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm font-medium">
                      {w.program_name}
                      {!w.is_active && <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">Archived</span>}
                    </p>
                    <p className="text-xs text-muted-foreground">{w.revenue_model}</p>
                  </div>
                )}
              </div>
              {editingId !== w.id && (
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" onClick={() => { setEditingId(w.id); setEditName(w.program_name); }}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  {w.is_active ? (
                    <Button size="sm" variant="outline" onClick={() => handleArchive(w.id, false)}>
                      <Archive className="mr-1.5 h-3.5 w-3.5" /> Archive
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => handleArchive(w.id, true)}>
                      <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Restore
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
